// START IMPORTS
import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import { capitalizeEachWord } from '@ltvco/refresh-lib/utils';
import {
  FeatureBlockedModal,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface FeatureGatedRouteProps {
  children: JSX.Element;
  featureKey?: string;
  reportTypeToFeature?: boolean;
}
// END INTERFACES_AND_TYPES

export function FeatureGatedRoute({
  children,
  featureKey,
  reportTypeToFeature = false,
}: FeatureGatedRouteProps) {
  // START HOOKS_AND_QUERIES
  const { getAccessType } = useSubscriptionAccessConfigContext();
  const navigate = useNavigate();
  const params = useParams();
  const {
    isFeatureBlockedModalOpen,
    closeFeatureBlockedModal,
    openFeatureBlockedModal,
  } = useFeatureBlockedModal();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [searchType, setSearchType] = useState('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  // END STATE_MANAGEMENT

  // START CONSTANTS
  const REPORT_TYPE_TO_FEATURE: Record<string, string> = {
    person: 'person',
    phone: 'phone',
    email: 'email',
    property: 'property',
    vehicle: 'vehicle',
    username: 'username',
    contact: 'contact',
    obituary: 'obituary',
    ancestor: 'ancestry',
    'phone-fraud': 'fraud_scan',
    'email-fraud': 'fraud_scan',
    'ip-fraud': 'fraud_scan',
    'url-fraud': 'fraud_scan',
  };
  // END CONSTANTS

  // START EVENT_HANDLERS
  const handleModalClose = () => {
    closeFeatureBlockedModal();
    navigate('/dashboard');
  };
  // END EVENT_HANDLERS

  // START DERIVED_DATA_AND_UTILS
  const actualFeatureKey =
    reportTypeToFeature && params.reportType
      ? REPORT_TYPE_TO_FEATURE[params.reportType]
      : featureKey;

  const currentAccessType = actualFeatureKey
    ? getAccessType(actualFeatureKey)
    : 'view';
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (!actualFeatureKey) {
      return;
    }

    if (currentAccessType === 'hide') {
      navigate('/dashboard');
    } else if (currentAccessType === 'block') {
      setSearchType(capitalizeEachWord(actualFeatureKey.replace('_', ' ')));
      openFeatureBlockedModal();
    }
  }, [
    actualFeatureKey,
    currentAccessType,
    navigate,
    openFeatureBlockedModal,
    setSearchType,
  ]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (currentAccessType === 'block') {
    return (
      <>
        <div
          style={{
            filter: 'blur(3px)',
            pointerEvents: 'none',
            userSelect: 'none',
            position: 'relative',
            opacity: 0.5,
          }}
        >
          {children}
        </div>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        />
        <FeatureBlockedModal
          isOpen={isFeatureBlockedModalOpen}
          onClose={handleModalClose}
          searchType={searchType}
          paymentShowModalNumber={paymentShowModalNumber}
          setPaymentShowModalNumber={setPaymentShowModalNumber}
        />
      </>
    );
  }

  if (currentAccessType === 'hide') {
    return null;
  }
  // END EARLY_RETURNS

  // START RENDER
  return children;
  // END RENDER
}
